import Spinner from '../ui/spinner';
import { Shield, MoreVertical, Search } from 'lucide-react';

import { useTranslation } from '../../i18n';
import useListParams from '../../hooks/useListParams';
import { getImageUrl } from '../../utils/imageUrl';
import { formatDateTime } from '../../utils/formatDate';
import { IBackofficeAdmin } from '../../types';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const PERMISSION_KEYS = [
  {
    key: 'users',
    labelKey: 'permissions.users',
  },
  {
    key: 'spaces',
    labelKey: 'permissions.spaces',
  },
  {
    key: 'feedback',
    labelKey: 'permissions.feedback',
  },
  {
    key: 'reports',
    labelKey: 'permissions.reports',
  },
  {
    key: 'x_posts',
    labelKey: 'permissions.x_posts',
  },
  {
    key: 'audit_logs',
    labelKey: 'permissions.audit_logs',
  },
  {
    key: 'settings',
    labelKey: 'permissions.settings',
  },
  {
    key: 'admins',
    labelKey: 'permissions.admins',
  },
];

interface IAdminListViewProps {
  admins: IBackofficeAdmin[];
  isLoading: boolean;
  currentUserId: string | null;
  isOwner: boolean;
  onViewDetail: (admin: IBackofficeAdmin) => void;
  onEditPermissions: (admin: IBackofficeAdmin) => void;
  onToggleActive: (admin: IBackofficeAdmin) => void;
  onDelete: (admin: IBackofficeAdmin) => void;
}

type EStatusFilter = 'all' | 'active' | 'disabled';

const AdminListView = ({
  admins,
  isLoading,
  currentUserId,
  isOwner,
  onViewDetail,
  onEditPermissions,
  onToggleActive,
  onDelete,
}: IAdminListViewProps) => {
  const { t } = useTranslation();
  const {
    search: searchQuery,
    filter,
    setSearch: setSearchQuery,
    setFilter,
  } = useListParams({ filter: 'all' });
  const statusFilter = (filter || 'all') as EStatusFilter;

  if (isLoading) {
    return <Spinner />;
  }

  const filteredAdmins = admins.filter((admin) => {
    // Status filter
    if (statusFilter === 'active' && !admin.isActive) return false;
    if (statusFilter === 'disabled' && admin.isActive) return false;

    // Search filter
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();

    return (
      admin.displayName.toLowerCase().includes(q) ||
      admin.username.toLowerCase().includes(q) ||
      (admin.email || '').toLowerCase().includes(q)
    );
  });

  const stats = {
    total: admins.length,
    active: admins.filter((a) => a.isActive).length,
    inactive: admins.filter((a) => !a.isActive).length,
  };

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {[
          {
            label: t('admin.total_admins'),
            value: stats.total,
            icon: <Shield className="w-8 h-8 text-blue-600" />,
          },
          {
            label: t('admin.active'),
            value: stats.active,
            icon: <Shield className="w-8 h-8 text-green-600" />,
          },
          {
            label: t('admin.disabled'),
            value: stats.inactive,
            icon: <Shield className="w-8 h-8 text-gray-600" />,
          },
        ].map(({ label, value, icon }) => (
          <Card key={label}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-muted-foreground">{label}</div>
                  <div className="text-2xl font-bold text-foreground mt-1">
                    {value}
                  </div>
                </div>
                {icon}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search & Filter */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <Search className="w-5 h-5" />
              </span>
              <Input
                type="text"
                placeholder={t('admin.search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              {(
                [
                  { key: 'all', label: t('common.all') },
                  { key: 'active', label: t('common.active') },
                  { key: 'disabled', label: t('admin.disabled') },
                ] as const
              ).map(({ key, label }) => (
                <Button
                  key={key}
                  variant={statusFilter === key ? 'default' : 'outline'}
                  onClick={() => setFilter(key)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="gap-0 overflow-hidden">
        <Table style={{ minWidth: '900px' }}>
          <TableHeader>
            <TableRow>
              <TableHead className="px-6">{t('admin.admin_col')}</TableHead>
              <TableHead className="px-6">{t('common.email')}</TableHead>
              <TableHead className="px-6">{t('permissions.column')}</TableHead>
              <TableHead className="px-6">{t('common.status')}</TableHead>
              <TableHead className="px-6">{t('admin.last_login')}</TableHead>
              <TableHead className="px-6 text-right">
                {t('common.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAdmins.map((admin) => {
              const isSelf = currentUserId === admin.id;

              return (
                <TableRow
                  key={admin.id}
                  onClick={() => !admin.isOwner && onViewDetail(admin)}
                  className={admin.isOwner ? '' : 'cursor-pointer'}
                >
                  <TableCell className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm overflow-hidden shrink-0">
                        {admin.avatar ? (
                          <img
                            src={getImageUrl(admin.avatar)!}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          admin.displayName.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {admin.displayName}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          @{admin.username}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-sm text-muted-foreground">
                    {admin.email || '—'}
                  </TableCell>
                  <TableCell className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {(admin.permissions ?? []).slice(0, 2).map((perm) => {
                        const permDef = PERMISSION_KEYS.find(
                          (p) => p.key === perm,
                        );

                        return (
                          <Badge key={perm} variant="outline">
                            {permDef ? t(permDef.labelKey) : perm}
                          </Badge>
                        );
                      })}
                      {(admin.permissions ?? []).length > 2 && (
                        <Badge variant="outline">
                          +{(admin.permissions ?? []).length - 2}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4">
                    <Badge variant={admin.isActive ? 'success' : 'muted'}>
                      {admin.isActive
                        ? t('common.active')
                        : t('admin.disabled')}
                    </Badge>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-xs text-muted-foreground">
                    {admin.updatedAt ? formatDateTime(admin.updatedAt) : '—'}
                  </TableCell>
                  <TableCell
                    className="px-6 py-4 text-right"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isOwner && !isSelf ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-gray-400 hover:text-foreground"
                          >
                            <MoreVertical className="w-5 h-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onViewDetail(admin)}>
                            {t('admin.view_detail')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onEditPermissions(admin)}
                          >
                            {t('admin.edit_permissions')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onToggleActive(admin)}
                          >
                            {admin.isActive
                              ? t('admin.disable')
                              : t('admin.enable')}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={() => onDelete(admin)}
                          >
                            {t('admin.delete_account')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : isSelf ? (
                      <span className="text-xs text-muted-foreground">
                        {t('admin.current_account')}
                      </span>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>
    </>
  );
};

export default AdminListView;
